import { json } from 'd3'

import i18n from 'live/services/i18n'

import { IBarrier, IBowtie, IPaginatedREST } from '../types'
import { BOWTIE_BASE_URL, BOWTIE_REST_ENDPOINTS } from '../utils/constants'
import { hasAdminPermission } from '../utils/resourcePermissions'
import { transformJsonConfigIntoObject, transformJsonConfigIntoString } from './barrier'

export interface IBowtiesListFilters {
    /** number of itens per page */
    perPage?: number
    /** page to request page start in 0 */
    page?: number
    /** field to orderBy */
    orderBy?: string
    /** orderBy Direction */
    direction?: 'ASC' | 'DESC'
    /** This will filter by operation id */
    filterBy?: number
    /** string to search */
    keyword?: string
    /** filter by operation */
    operation?: string
    /** filter by asset */
    asset?: string
}

export interface IBowtieCreation {
    bowtieTemplateId: number
    bowtieCode: string
    asset: {
        id: number
    }
    methodType: string
}

export const listBowties = async (params?: IBowtiesListFilters): Promise<IPaginatedREST<IBowtie>> => {
    const qs = new URLSearchParams({
        limit: params?.perPage?.toString() ?? '',
        offset: (Math.max(1, params?.page ?? 0) - 1)?.toString() ?? '0',
        keyword: params?.keyword?.toString() ?? '',
        orderBy: params?.orderBy?.toString()?.toUpperCase() ?? 'ID',
        direction: params?.direction?.toString() ?? 'ASC',
        operation: params?.operation?.toString() ?? '',
        asset: params?.asset?.toString() ?? '',
        resume: 'true'
    }).toString()

    return fetch(
        hasAdminPermission
            ? `${BOWTIE_BASE_URL}/${BOWTIE_REST_ENDPOINTS.bowtie}/admin?${qs}`
            : `${BOWTIE_BASE_URL}/${BOWTIE_REST_ENDPOINTS.bowtie}/?${qs}`,
        {
            method: 'GET',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' }
        }
    ).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            jsonData?.data?.forEach(bowtie => {
                transformBowtieBarriersJsonConfigIntoObject(bowtie)
            })

            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
        }
    })
}

export const listAllBowtiesWithDetails = async (assetFilter?: number[]): Promise<IPaginatedREST<IBowtie>> => {
    const qs = new URLSearchParams({})
    if (assetFilter) {
        assetFilter.forEach(asset => {
            qs.append('filterByAsset', asset.toString())
        })
    }

    return fetch(
        hasAdminPermission
            ? `${BOWTIE_BASE_URL}/${BOWTIE_REST_ENDPOINTS.bowtie}/list_all/admin?${qs.toString()}`
            : `${BOWTIE_BASE_URL}/${BOWTIE_REST_ENDPOINTS.bowtie}/list_all?${qs.toString()}`,
        {
            method: 'GET',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' }
        }
    ).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            jsonData?.data?.forEach(bowtie => {
                transformBowtieBarriersJsonConfigIntoObject(bowtie)
            })

            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
        }
    })
}

export const listAllBowtiesMethods = async (): Promise<string[]> => {
    return fetch(`${BOWTIE_BASE_URL}/${BOWTIE_REST_ENDPOINTS.bowtie}/list_methods`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
        }
    })
}

export const getBowtie = async (id: number): Promise<IBowtie> => {
    if (!id) {
        return undefined
    }

    return fetch(`${BOWTIE_BASE_URL}/${BOWTIE_REST_ENDPOINTS.bowtie}/${id}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            transformBowtieBarriersJsonConfigIntoObject(jsonData)
            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
        }
    })
}

export const createBowtie = async <COPY extends boolean = false>(
    data: COPY extends true ? { id: number } : IBowtieCreation,
    copy?: COPY
): Promise<IBowtie> => {
    return fetch(`${BOWTIE_BASE_URL}/${BOWTIE_REST_ENDPOINTS.bowtie}/${copy ? '?copy=true' : ''}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
        }
    })
}

const transformBowtieBarriersJsonConfigIntoObject = (bowtie: IBowtie): void => {
    if (bowtie?.topEvent?.threats) {
        for (const threat of bowtie.topEvent.threats) {
            for (const threatBar of threat.barriers) {
                transformJsonConfigIntoObject(threatBar)
            }
        }
    }
    if (bowtie?.topEvent?.consequences) {
        for (const conseq of bowtie.topEvent.consequences) {
            for (const conseqtBar of conseq.barriers) {
                transformJsonConfigIntoObject(conseqtBar)
            }
        }
    }
}

const transformBowtieBarriersJsonConfigIntoString = (bowtie: IBowtie): void => {
    if (bowtie?.topEvent?.threats) {
        for (const threat of bowtie.topEvent.threats) {
            for (const threatBar of threat.barriers) {
                transformJsonConfigIntoString(threatBar)
            }
        }
    }
    if (bowtie?.topEvent?.consequences) {
        for (const conseq of bowtie.topEvent.consequences) {
            for (const conseqtBar of conseq.barriers) {
                transformJsonConfigIntoString(conseqtBar)
            }
        }
    }
}

export const updateBowtie = async (bowtie: IBowtie): Promise<IBowtie> => {
    transformBowtieBarriersJsonConfigIntoString(bowtie)

    return fetch(`${BOWTIE_BASE_URL}/${BOWTIE_REST_ENDPOINTS.bowtie}/`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bowtie)
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
        }
    })
}

export const deleteBowtie = async (id: number): Promise<void> => {
    return fetch(`${BOWTIE_BASE_URL}/${BOWTIE_REST_ENDPOINTS.bowtie}/${id}`, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message ?? i18n('Error on delete bowtie'))
        }
    })
}
